/**
 * Draft persistence.
 *
 * A supervisor entering a crew day on a job site can lose the tab to a phone
 * call, a screen lock, or the OS reclaiming memory. Losing 6 people's hours to
 * that would be the fastest way to lose adoption, so every keystroke is
 * mirrored to localStorage and restored on return.
 *
 * This is also the groundwork for the offline queue in week 4 — the draft
 * shape is already what gets posted, so queueing it is a storage change rather
 * than a rewrite.
 */

import type { EntryType } from '@/api/types'

// Versioned: the line shape changed when cost code and earnings code were
// replaced by a single work type, and again when shifts and task narratives
// were added. An old draft would deserialise into something the entry screen
// cannot render.
const DRAFT_KEY = 'nwl.draft.submission.v3'
const CREW_MEMORY_KEY = 'nwl.memory.crew'

export interface DraftLine {
  id: string
  sageEmployeeKey: string
  /** The one thing the field chooses. Cost and earnings codes derive from it. */
  workTypeId: number
  entryType: EntryType
  laborCode: string | null
  hours: string
  amount: string
  /**
   * Set only when this line was worked OUT OF TRADE — a laborer running
   * equipment on a prevailing job. Null means the person's own classification,
   * which is every ordinary line. Rate follows the work performed, not the job
   * title, so this is a fact about the hours and not about the person.
   */
  wageClassificationId: number | null
  /**
   * Set only when this line is charged to a job OTHER than the crew-day's —
   * travel at the end of a shift belongs to the job they are driving TO, not
   * the one they are leaving. Null or absent means the crew-day's job, which
   * is every ordinary line.
   *
   * OPTIONAL on purpose. Adding it did not bump DRAFT_KEY: an older draft
   * simply has no such field, `undefined` reads the same as null everywhere it
   * is used, and bumping the key would have thrown away half-entered crew days
   * sitting on phones.
   */
  overrideJobNo?: string | null
}

/**
 * One person's shift and task note, when they differ from the crew's.
 *
 * Only holds OVERRIDES. Blank means "same as the crew", which is how the paper
 * sheet reads — one shift written once, most task cells empty, and a note
 * against the two people whose day went differently.
 */
export interface DraftShift {
  /** 24h "HH:MM", or '' to inherit the crew's. */
  start: string
  end: string
  note: string
}

export interface Draft {
  submissionId: string
  jobNo: string
  dateWorked: string
  crewKeys: string[]
  lines: DraftLine[]
  note: string

  /** What the whole crew did. Optional; blank records nothing. */
  workPerformed: string
  /** The crew's shift. Applies to everyone without an override. */
  crewStart: string
  crewEnd: string
  /** Per-person overrides, keyed by sageEmployeeKey. Sparse by design. */
  shifts: Record<string, DraftShift>

  /**
   * Who drove, on this crew day. Not a property of the employee — the same man
   * drives to one job and rides to the next — so it is captured per day and
   * starts empty every time rather than being remembered like the crew list.
   *
   * Decides whether that person's travel hours pay the driver code or the
   * rider code, which the server resolves on submit.
   */
  drivers: string[]

  startedUtc: string

  /**
   * WHERE THE SUPERVISOR WAS, not just what they typed.
   *
   * Both of these are view state, and both were previously plain useState —
   * so a refresh restored every hour entered and still dropped the supervisor
   * back on step 1 with the crew picker reset to all 138 employees. Nothing
   * was lost, but it looked exactly like everything was, which on a job site
   * is the same thing: they start again.
   *
   * OPTIONAL, and no DRAFT_KEY bump. An older draft simply resumes at step 1
   * as it always did, which is why throwing away in-progress crew days to add
   * this would have been the wrong trade.
   */
  /**
   * The "on site" reading, held here until the day is sent.
   *
   * In the DRAFT rather than posted on the tap, because a job site with no
   * signal is the normal case. capturedUtc is the device clock at the moment
   * of the tap and is the only timestamp that means anything; the server
   * records separately when it actually arrived, so a reading taken at 07:10
   * and synced at 19:40 stays honest about both.
   *
   * Optional, like everything else added after v3 shipped.
   */
  /**
   * Why there is no "on site" reading, when there is none. Asked of a
   * supervisor at send time -- either a reading or a reason -- ahead of the
   * check-in becoming mandatory. Optional here, like every field added after
   * v3, so an older saved draft loads untouched.
   */
  noCheckInReason?: string

  checkIn?: {
    /**
     * The job this reading was taken FOR.
     *
     * Held with the reading rather than assumed from draft.jobNo, because the
     * two can diverge: tap on job A, change your mind, pick job B, and the
     * coordinates from A would be posted against B. Recorded here so a change
     * of job can drop it.
     */
    jobNo: string
    latitude: number
    longitude: number
    /** Metres. Null when the device would not say — worth knowing. */
    accuracyMeters: number | null
    capturedUtc: string
  } | null

  step?: 1 | 2
  /** Supervisor whose crew filters the roster; null means the whole roster. */
  pickedCrewId?: number | null
}

export function newDraft(): Draft {
  return {
    // Generated up front, before anything is sent. This is what makes a
    // replayed submit idempotent rather than duplicating a crew day.
    submissionId: crypto.randomUUID(),
    jobNo: '',
    dateWorked: todayLocalISO(),
    crewKeys: [],
    lines: [],
    note: '',
    workPerformed: '',
    crewStart: '',
    crewEnd: '',
    shifts: {},
    drivers: [],
    step: 1,
    pickedCrewId: null,
    startedUtc: new Date().toISOString(),
  }
}

/** Local calendar date, not UTC — a 7pm shift must not record as tomorrow. */
export function todayLocalISO(): string {
  const d = new Date()
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60_000)
  return local.toISOString().slice(0, 10)
}

export function loadDraft(): Draft | null {
  try {
    const raw = localStorage.getItem(DRAFT_KEY)
    if (!raw) return null
    const d = JSON.parse(raw) as Draft
    if (!d.submissionId) return null
    // Added after v3 shipped. Defaulted rather than version-bumped: a missing
    // driver list is harmless, and discarding a half-entered crew day on a job
    // site to avoid it would be the worse trade.
    d.drivers ??= []
    // Same treatment, same reason: a draft saved before out-of-trade lines
    // existed has no classification on its lines. Undefined would survive as
    // far as the payload, where it reads as "not sent" rather than "the
    // person's own trade" — true today, but only by accident. Defaulted here
    // so it stays true on purpose.
    for (const l of d.lines ?? []) l.wageClassificationId ??= null
    d.pickedCrewId ??= null
    // Resume on the hours step only if there is something to show. A draft
    // carrying step 2 and no lines would strand the supervisor on an empty
    // screen with no way back to the crew picker.
    d.step = d.step === 2 && (d.lines?.length ?? 0) > 0 ? 2 : 1
    return d
  } catch {
    return null
  }
}

export function saveDraft(draft: Draft): void {
  try {
    localStorage.setItem(DRAFT_KEY, JSON.stringify(draft))
  } catch {
    // Storage full or blocked. The in-memory draft still works; losing the
    // backup is not worth interrupting entry over.
  }
}

export function clearDraft(): void {
  localStorage.removeItem(DRAFT_KEY)
}

/**
 * Remember who worked a job last time.
 *
 * Crews are stable — the same people return to the same job day after day.
 * Recalling last time's crew turns the slowest step (picking 6 of 138 names)
 * into a single confirm, which is where most of the 60-second target is won.
 */
export function rememberCrew(jobNo: string, crewKeys: string[]): void {
  if (!jobNo || crewKeys.length === 0) return
  try {
    const all = JSON.parse(localStorage.getItem(CREW_MEMORY_KEY) ?? '{}') as Record<
      string,
      string[]
    >
    all[jobNo] = crewKeys
    localStorage.setItem(CREW_MEMORY_KEY, JSON.stringify(all))
  } catch {
    /* non-fatal */
  }
}

export function recallCrew(jobNo: string): string[] {
  try {
    const all = JSON.parse(localStorage.getItem(CREW_MEMORY_KEY) ?? '{}') as Record<
      string,
      string[]
    >
    return all[jobNo] ?? []
  } catch {
    return []
  }
}
